import React from 'react';
import { usePublicCompany } from '../hooks/usePublicCompany';
import LegalFooter from '../components/LegalFooter';

/**
 * Pagina pubblica con la Cookie Policy della piattaforma web. Usiamo solo
 * strumenti tecnici (autenticazione e preferenze), quindi secondo le linee guida
 * del Garante non serve consenso preventivo né banner.
 * I dati del titolare arrivano da /api/public/company.
 */

const UPDATED = '25 agosto 2026';

const Section: React.FC<{ n: string; title: string; children: React.ReactNode }> = ({ n, title, children }) => (
    <section className="mb-8">
        <h2 className="text-lg font-black text-slate-900 mb-3">
            <span className="text-rose-600">{n}.</span> {title}
        </h2>
        <div className="space-y-3 text-[14px] leading-relaxed text-slate-700">{children}</div>
    </section>
);

const CookiePolicyView: React.FC = () => {
    const company = usePublicCompany();
    const CONTACT_EMAIL = company.email;

    return (
        <div className="min-h-screen bg-slate-50 py-10 px-4">
            <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 p-8 sm:p-12">
                <header className="mb-8 pb-6 border-b border-slate-200">
                    <p className="text-[11px] font-black uppercase tracking-widest text-rose-600 mb-2">Ultra Detective</p>
                    <h1 className="text-2xl sm:text-3xl font-black text-slate-900">Cookie Policy</h1>
                    <p className="text-[13px] text-slate-500 mt-2">Ultimo aggiornamento: {UPDATED}</p>
                </header>

                <p className="text-[14px] leading-relaxed text-slate-700 mb-8">
                    La presente Cookie Policy descrive l'uso di cookie e tecnologie analoghe da parte della
                    piattaforma web <strong>Ultra Detective</strong> (la “Piattaforma”), gestionale professionale
                    ad accesso riservato agli operatori di <strong>{company.agencyName}</strong>. È redatta ai sensi
                    del Regolamento (UE) 2016/679 (“GDPR”), dell'art. 122 del D.Lgs. 196/2003 e delle “Linee guida
                    cookie e altri strumenti di tracciamento” del Garante Privacy del 10 giugno 2021.
                </p>

                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-8 text-[14px] text-emerald-900">
                    <strong>In sintesi:</strong> la Piattaforma utilizza <strong>solo strumenti tecnici</strong>
                    {' '}necessari al funzionamento e alla sicurezza del servizio. <strong>Non</strong> impiega cookie
                    di profilazione, analitici o pubblicitari e <strong>non</strong> traccia gli utenti. Per questo
                    motivo <strong>non è richiesto alcun consenso</strong> e non viene mostrato alcun banner cookie.
                </div>

                <Section n="1" title="Cosa sono i cookie">
                    <p>
                        I cookie sono piccoli file di testo che i siti visitati inviano al dispositivo dell'utente,
                        dove vengono memorizzati per essere ritrasmessi agli stessi siti alla visita successiva.
                        Tecnologie analoghe (es. <em>localStorage</em> e <em>sessionStorage</em>) consentono di
                        memorizzare informazioni nel browser per finalità equivalenti. Nel seguito il termine
                        “cookie” è usato in senso ampio per ricomprendere anche tali strumenti.
                    </p>
                </Section>

                <Section n="2" title="Tipologie di cookie utilizzati">
                    <p>La Piattaforma utilizza esclusivamente le seguenti categorie di strumenti tecnici:</p>
                    <ul className="list-disc pl-5 space-y-2">
                        <li>
                            <strong>Strumenti tecnici di autenticazione e sessione:</strong> memorizzano nel browser
                            il token di accesso (JWT) e lo stato della sessione, per mantenere l'utente autenticato e
                            garantire la sicurezza dell'area riservata. Senza di essi il servizio non può funzionare.
                        </li>
                        <li>
                            <strong>Strumenti tecnici di preferenza:</strong> memorizzano scelte dell'utente come il
                            tema chiaro/scuro, il codice azienda o l'ultima sezione visitata, per migliorare
                            l'esperienza d'uso.
                        </li>
                        <li>
                            <strong>Strumenti tecnici di sicurezza (ove attivi):</strong> su alcune installazioni la
                            connessione è protetta da <strong>Cloudflare</strong>, che può impostare cookie tecnici
                            strettamente necessari alla sicurezza e al controllo degli accessi.
                        </li>
                    </ul>
                    <p>
                        Tali strumenti sono qualificati come <strong>“tecnici”</strong> ai sensi dell'art. 122 del
                        Codice Privacy e, come chiarito dal Garante, <strong>non necessitano del consenso</strong>
                        {' '}dell'utente.
                    </p>
                </Section>

                <Section n="3" title="Cookie che NON utilizziamo">
                    <p>La Piattaforma <strong>non</strong> utilizza:</p>
                    <ul className="list-disc pl-5 space-y-2">
                        <li>cookie o SDK <strong>di profilazione</strong> o marketing;</li>
                        <li>cookie <strong>analitici</strong> di terze parti (es. Google Analytics);</li>
                        <li>cookie di <strong>social network</strong> o pulsanti di condivisione;</li>
                        <li>tecnologie di <strong>tracciamento</strong> dell'utente attraverso siti o app di terzi.</li>
                    </ul>
                </Section>

                <Section n="4" title="Durata">
                    <p>
                        I cookie di sessione vengono cancellati alla chiusura del browser. Gli strumenti di
                        autenticazione e preferenza persistono per il tempo tecnicamente necessario a mantenere
                        l'accesso e le impostazioni, e vengono rimossi al logout o allo scadere del token.
                    </p>
                </Section>

                <Section n="5" title="Gestione e disattivazione">
                    <p>
                        Trattandosi di strumenti tecnici, la loro disattivazione può compromettere il corretto
                        funzionamento della Piattaforma (ad esempio impedendo l'accesso all'area riservata).
                        L'utente può in ogni caso gestire o eliminare i cookie tramite le impostazioni del proprio
                        browser; le relative istruzioni sono disponibili nelle guide dei principali browser
                        (Chrome, Firefox, Safari, Edge).
                    </p>
                </Section>

                <Section n="6" title="Titolare e contatti">
                    <p>
                        Titolare del trattamento è <strong>{company.agencyName}</strong>
                        {company.fullAddress ? `, ${company.fullAddress}` : ''}
                        {company.vatNumber ? ` — P.IVA ${company.vatNumber}` : ''}. Per informazioni sull'uso dei
                        cookie e per l'esercizio dei diritti previsti dagli artt. 15-22 GDPR è possibile scrivere a{' '}
                        <a className="text-rose-600 underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
                        Per il trattamento dei dati personali si rinvia all'
                        <a className="text-rose-600 underline" href="/privacy-policy">Informativa sulla Privacy</a>.
                    </p>
                </Section>

                <Section n="7" title="Modifiche alla presente policy">
                    <p>
                        La presente Cookie Policy può essere aggiornata nel tempo. La versione vigente è sempre
                        pubblicata a questo indirizzo, con indicazione della data di ultimo aggiornamento.
                    </p>
                </Section>

                <footer className="mt-10 pt-6 border-t border-slate-200 text-[12px] text-slate-400">
                    {company.agencyName} — Cookie Policy — {UPDATED}
                </footer>

                <LegalFooter />
            </div>
        </div>
    );
};

export default CookiePolicyView;
